/**
 * TouchControls — first-person touch input for the "go [ into ] it" player.
 *
 * Pattern follows the de-facto mobile FPS convention (Hyperfy, Spark's
 * mobile-joystick example, most mobile games):
 *
 *   - LEFT half of the screen: a floating joystick. It appears where the
 *     thumb lands (no fixed dead corner), drives walk forward/strafe.
 *   - RIGHT half of the screen: drag to look (yaw/pitch).
 *   - A short tap (no significant movement, < 250 ms) fires an interact
 *     callback with the tap position in normalized device coordinates.
 *
 * The module owns its own DOM (joystick base + knob) and emits nothing per
 * frame — the runtime polls `state` in its tick. All listeners are passive
 * where possible; the container gets `touch-action: none` so the browser
 * never scrolls/zooms while playing.
 */

export interface TouchControlsState {
  /** Strafe -1..1 (right positive). */
  moveX: number;
  /** Walk -1..1 (forward positive). */
  moveY: number;
  /** Whether the move joystick is currently held. */
  moveActive: boolean;
  /** Accumulated look delta in CSS px since last consume (yaw axis). */
  lookDX: number;
  /** Accumulated look delta in CSS px since last consume (pitch axis). */
  lookDY: number;
  /** Whether a look drag is in progress. */
  lookActive: boolean;
}

export interface TouchTap {
  /** Normalized device coords -1..1 (Three.js raycaster convention). */
  ndcX: number;
  ndcY: number;
}

export type TouchActivityKind = 'look' | 'move' | 'tap' | 'start';

const JOYSTICK_SIZE = 124;
const KNOB_SIZE = 52;
const DEADZONE = 0.12;
const TAP_MAX_MS = 250;
const TAP_MAX_MOVE_PX = 12;

export class TouchControls {
  readonly state: TouchControlsState = {
    moveX: 0,
    moveY: 0,
    moveActive: false,
    lookDX: 0,
    lookDY: 0,
    lookActive: false,
  };

  private container: HTMLElement;
  private root: HTMLDivElement;
  private knob: HTMLDivElement;
  private onTap?: (tap: TouchTap) => void;
  private onActivity?: (kind: TouchActivityKind) => void;

  private moveTouchId: number | null = null;
  private moveOriginX = 0;
  private moveOriginY = 0;

  private lookTouchId: number | null = null;
  private lookLastX = 0;
  private lookLastY = 0;
  private lookStartX = 0;
  private lookStartY = 0;
  private lookStartTime = 0;
  private lookMovedPx = 0;

  private disposed = false;

  constructor(
    container: HTMLElement,
    opts?: { onTap?: (tap: TouchTap) => void; onActivity?: (kind: TouchActivityKind) => void },
  ) {
    this.container = container;
    this.onTap = opts?.onTap;
    this.onActivity = opts?.onActivity;

    // Joystick DOM — hidden until a left-side touch lands.
    this.root = document.createElement('div');
    this.root.className = 'touch-joystick';
    this.root.style.cssText = [
      'position:absolute',
      `width:${JOYSTICK_SIZE}px`,
      `height:${JOYSTICK_SIZE}px`,
      'border-radius:50%',
      'background:rgba(255,255,255,0.07)',
      'border:1.5px solid rgba(255,255,255,0.28)',
      'backdrop-filter:blur(2px)',
      'pointer-events:none',
      'z-index:30',
      'display:none',
      'transform:translate(-50%,-50%)',
    ].join(';');
    this.knob = document.createElement('div');
    this.knob.style.cssText = [
      'position:absolute',
      `width:${KNOB_SIZE}px`,
      `height:${KNOB_SIZE}px`,
      'border-radius:50%',
      'background:rgba(255,255,255,0.38)',
      'border:1px solid rgba(255,255,255,0.5)',
      'left:50%',
      'top:50%',
      'transform:translate(-50%,-50%)',
    ].join(';');
    this.root.appendChild(this.knob);
    container.appendChild(this.root);

    // touch-action:none stops the browser hijacking drags for scroll/zoom.
    container.style.touchAction = 'none';

    container.addEventListener('touchstart', this.onTouchStart, { passive: false });
    container.addEventListener('touchmove', this.onTouchMove, { passive: false });
    container.addEventListener('touchend', this.onTouchEnd, { passive: false });
    container.addEventListener('touchcancel', this.onTouchEnd, { passive: false });
  }

  /** Read-and-reset accumulated look deltas (call once per tick). */
  consumeLookDelta(out: { dx: number; dy: number } = { dx: 0, dy: 0 }): { dx: number; dy: number } {
    out.dx = this.state.lookDX;
    out.dy = this.state.lookDY;
    this.state.lookDX = 0;
    this.state.lookDY = 0;
    return out;
  }

  private onTouchStart = (e: TouchEvent) => {
    const rect = this.container.getBoundingClientRect();
    for (const touch of Array.from(e.changedTouches)) {
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;
      const isLeftHalf = x < rect.width / 2;

      if (isLeftHalf && this.moveTouchId === null) {
        this.onActivity?.('start');
        // Floating joystick: anchor where the thumb lands.
        this.moveTouchId = touch.identifier;
        this.moveOriginX = x;
        this.moveOriginY = y;
        this.root.style.left = `${x}px`;
        this.root.style.top = `${y}px`;
        this.root.style.display = 'block';
        this.state.moveActive = true;
        this.setKnob(0, 0);
        e.preventDefault();
      } else if (!isLeftHalf && this.lookTouchId === null) {
        this.onActivity?.('start');
        this.lookTouchId = touch.identifier;
        this.lookLastX = touch.clientX;
        this.lookLastY = touch.clientY;
        this.lookStartX = touch.clientX;
        this.lookStartY = touch.clientY;
        this.lookStartTime = performance.now();
        this.lookMovedPx = 0;
        this.state.lookActive = true;
        e.preventDefault();
      }
    }
  };

  private onTouchMove = (e: TouchEvent) => {
    const rect = this.container.getBoundingClientRect();
    for (const touch of Array.from(e.changedTouches)) {
      if (touch.identifier === this.moveTouchId) {
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        const maxR = (JOYSTICK_SIZE - KNOB_SIZE) / 2;
        let dx = x - this.moveOriginX;
        let dy = y - this.moveOriginY;
        const dist = Math.hypot(dx, dy);
        if (dist > maxR) {
          dx = (dx / dist) * maxR;
          dy = (dy / dist) * maxR;
        }
        this.setKnob(dx, dy);
        let ix = dx / maxR;
        let iy = dy / maxR;
        if (Math.abs(ix) < DEADZONE) ix = 0;
        if (Math.abs(iy) < DEADZONE) iy = 0;
        this.state.moveX = ix;
        // Screen up = forward.
        this.state.moveY = -iy;
        if (ix !== 0 || iy !== 0) this.onActivity?.('move');
        e.preventDefault();
      } else if (touch.identifier === this.lookTouchId) {
        const dx = touch.clientX - this.lookLastX;
        const dy = touch.clientY - this.lookLastY;
        this.lookLastX = touch.clientX;
        this.lookLastY = touch.clientY;
        this.state.lookDX += dx;
        this.state.lookDY += dy;
        if (dx !== 0 || dy !== 0) this.onActivity?.('look');
        this.lookMovedPx = Math.max(
          this.lookMovedPx,
          Math.hypot(touch.clientX - this.lookStartX, touch.clientY - this.lookStartY),
        );
        e.preventDefault();
      }
    }
  };

  private onTouchEnd = (e: TouchEvent) => {
    const rect = this.container.getBoundingClientRect();
    for (const touch of Array.from(e.changedTouches)) {
      if (touch.identifier === this.moveTouchId) {
        this.moveTouchId = null;
        this.state.moveX = 0;
        this.state.moveY = 0;
        this.state.moveActive = false;
        this.root.style.display = 'none';
      } else if (touch.identifier === this.lookTouchId) {
        const elapsed = performance.now() - this.lookStartTime;
        // Short, near-stationary press = a tap → interact at that point.
        if (elapsed < TAP_MAX_MS && this.lookMovedPx < TAP_MAX_MOVE_PX && this.onTap) {
          const ndcX = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
          const ndcY = -(((touch.clientY - rect.top) / rect.height) * 2 - 1);
          this.onActivity?.('tap');
          this.onTap({ ndcX, ndcY });
        }
        this.lookTouchId = null;
        this.state.lookActive = false;
      }
    }
  };

  private setKnob(dx: number, dy: number): void {
    this.knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.container.removeEventListener('touchstart', this.onTouchStart);
    this.container.removeEventListener('touchmove', this.onTouchMove);
    this.container.removeEventListener('touchend', this.onTouchEnd);
    this.container.removeEventListener('touchcancel', this.onTouchEnd);
    this.root.remove();
  }
}

/**
 * True for touch-first devices (phones/tablets) — NOT merely touch-capable
 * laptops, which should keep pointer-lock controls. VR headsets excluded.
 */
export function isTouchPrimaryDevice(): boolean {
  if (/OculusBrowser|Quest/i.test(navigator.userAgent)) return false;
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const noHover = window.matchMedia('(hover: none)').matches;
  return coarse && noHover && navigator.maxTouchPoints > 0;
}

export interface DeviceSupport {
  supported: boolean;
  reason?: string;
}

/**
 * Conservative capability gate. We refuse only devices that genuinely cannot
 * run the player (no WebGL2 = Spark cannot start) rather than guessing at
 * performance — the adaptive quality system handles slow-but-capable devices.
 */
export function checkDeviceSupport(): DeviceSupport {
  let gl: WebGL2RenderingContext | null = null;
  try {
    const canvas = document.createElement('canvas');
    gl = canvas.getContext('webgl2');
  } catch {
    gl = null;
  }
  if (!gl) {
    return {
      supported: false,
      reason: 'This experience needs WebGL 2, which your browser or device does not support.',
    };
  }
  // Spark needs float color buffers + reasonably sized textures for the splat
  // data textures. Practically every WebGL2 device passes; very old GPUs don't.
  const maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
  if (maxTex < 4096) {
    return {
      supported: false,
      reason: 'Your graphics hardware is below the minimum needed for this experience.',
    };
  }
  return { supported: true };
}
